import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Plus, Trash2, Navigation, CheckCircle2, AlertTriangle } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useStoreStatus } from "../context/StoreStatusContext";
import { api } from "../lib/api";
import PinDropMap from "../components/PinDropMap";

export default function CheckoutPage() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const { isOpen: storeOpen } = useStoreStatus();
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ label: "Home", line1: "", line2: "", city: "Dimapur", pincode: "797113", phone: "", lat: null, lng: null });
  const [payment, setPayment] = useState("COD");
  const [submitting, setSubmitting] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [quote, setQuote] = useState(null); // {distance_km, delivery_fee, within_service_area, tier, message}
  const [quoting, setQuoting] = useState(false);
  const [locating, setLocating] = useState(false);
  const [formQuote, setFormQuote] = useState(null); // live quote while picking a pin
  const [restaurant, setRestaurant] = useState({ lat: 25.8944, lng: 93.7253 });
  const quoteDebounceRef = useRef(null);

  useEffect(() => {
    if (!user) { navigate("/auth?returnTo=/checkout"); return; }
    if (items.length === 0) { navigate("/menu"); return; }
    api.get("/addresses").then((r) => {
      setAddresses(r.data);
      if (r.data.length > 0) setSelected(r.data[0].id);
      else setShowNew(true);
    });
    api.get("/delivery/tiers").then((r) => { setTiers(r.data.tiers); setRestaurant(r.data.restaurant); });
  }, [user, items.length, navigate]);

  // Live-quote when the pin is moved on the map inside the new-address form
  const handlePinChange = (c) => {
    setForm((f) => ({ ...f, lat: c.lat, lng: c.lng }));
    if (quoteDebounceRef.current) clearTimeout(quoteDebounceRef.current);
    quoteDebounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/delivery/quote", { lat: c.lat, lng: c.lng });
        setFormQuote(data);
      } catch {
        setFormQuote(null);
      }
    }, 400);
  };

  // Re-quote whenever selected address changes
  useEffect(() => {
    const addr = addresses.find((a) => a.id === selected);
    if (!addr || addr.lat == null || addr.lng == null) { setQuote(null); return; }
    setQuoting(true);
    api.post("/delivery/quote", { lat: addr.lat, lng: addr.lng })
      .then((r) => setQuote(r.data))
      .catch(() => setQuote(null))
      .finally(() => setQuoting(false));
  }, [selected, addresses]);

  const useCurrentLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setForm((f) => ({ ...f, lat: p.coords.latitude, lng: p.coords.longitude }));
        toast.success("Location captured");
        setLocating(false);
      },
      (err) => { toast.error(err.message || "Could not get location"); setLocating(false); },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const saveAddress = async () => {
    if (!form.line1 || !form.phone) { toast.error("Address & phone required"); return; }
    if (form.lat == null || form.lng == null) {
      toast.error("Please tap 'Use my location' so we can compute your delivery fee");
      return;
    }
    // Pre-check delivery zone before saving
    try {
      const { data } = await api.post("/delivery/quote", { lat: form.lat, lng: form.lng });
      if (!data.within_service_area) { toast.error(data.message || "Outside delivery zone"); return; }
    } catch {}

    const { data } = await api.post("/addresses", form);
    setAddresses((c) => [...c, data]);
    setSelected(data.id);
    setShowNew(false);
    setForm({ label: "Home", line1: "", line2: "", city: "Dimapur", pincode: "797113", phone: "", lat: null, lng: null });
    toast.success("Address saved");
  };

  const delAddress = async (id) => {
    await api.delete(`/addresses/${id}`);
    setAddresses((c) => c.filter((a) => a.id !== id));
    if (selected === id) setSelected(null);
  };

  const placeOrder = async () => {
    if (!storeOpen) { toast.error("Sorry, we are closed today!"); return; }
    const addr = addresses.find((a) => a.id === selected);
    if (!addr) { toast.error("Please select an address"); return; }
    if (!quote || !quote.within_service_area) { toast.error("Address is outside our delivery zone"); return; }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id, name: i.name, price: i.price, qty: i.qty,
          variant: i.variant, note: i.note, image: i.image,
        })),
        address: {
          label: addr.label, line1: addr.line1, line2: addr.line2 || "", city: addr.city, pincode: addr.pincode,
          phone: addr.phone, lat: addr.lat, lng: addr.lng,
        },
        payment_method: payment, delivery_fee: quote.delivery_fee, tax: 0,
      };
      const { data } = await api.post("/orders", payload);
      clear();
      toast.success("Order placed! 🎉");
      navigate(`/track/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place order");
    } finally { setSubmitting(false); }
  };

  const deliveryFee = quote?.delivery_fee ?? 0;
  const total = subtotal + deliveryFee;
  const canPlace = storeOpen && quote?.within_service_area && selected;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-10" data-testid="checkout-page">
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400 mb-2">Checkout</div>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Almost there.</h1>

      {/* Delivery tier transparency banner */}
      {tiers.length > 0 && (
        <div className="mt-6 rounded-2xl bg-[#F7F7F7] border border-gray-100 p-4 md:p-5" data-testid="delivery-tiers">
          <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2">Delivery charges</div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {tiers.map((t) => (
              <div key={t.max_km} className={`rounded-xl p-3 border ${quote?.delivery_fee === t.fee ? "border-black bg-white" : "border-transparent"}`}>
                <div className="font-bold">{t.label}</div>
                <div className="text-[11px] text-gray-500">{t.description || `≤ ${t.max_km} km`}</div>
                <div className="font-display font-black text-lg mt-0.5">₹{t.fee}</div>
              </div>
            ))}
          </div>
          <div className="text-xs text-gray-500 mt-2">We deliver within 15 km of our Dimapur kitchen. Distance is calculated on real roads.</div>
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_420px] gap-10 mt-6">
        <div className="space-y-10">
          {/* Addresses */}
          <section>
            <h2 className="font-display font-bold text-xl mb-4">Delivery address</h2>
            <div className="space-y-3">
              {addresses.map((a) => (
                <label key={a.id} className={`flex items-start gap-3 border-2 rounded-2xl p-4 cursor-pointer transition ${selected === a.id ? "border-black bg-gray-50" : "border-gray-100 hover:border-gray-300"}`} data-testid={`address-${a.id}`}>
                  <input type="radio" checked={selected === a.id} onChange={() => setSelected(a.id)} className="mt-1 accent-black" />
                  <div className="flex-1">
                    <div className="font-bold text-sm">{a.label}</div>
                    <div className="text-sm text-gray-600">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city} – {a.pincode}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{a.phone}</div>
                  </div>
                  <button onClick={(e) => { e.preventDefault(); delAddress(a.id); }} className="text-gray-400 hover:text-black"><Trash2 className="w-4 h-4" /></button>
                </label>
              ))}

              {!showNew && (
                <button onClick={() => setShowNew(true)} className="border-2 border-dashed border-gray-300 rounded-2xl p-4 w-full text-left hover:border-black inline-flex items-center gap-2 text-sm font-bold" data-testid="add-address-btn">
                  <Plus className="w-4 h-4" /> Add new address
                </button>
              )}

              {showNew && (
                <div className="border-2 border-black rounded-2xl p-5 space-y-4">
                  {/* Interactive pin-drop map */}
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2 flex items-center justify-between">
                      <span>Drop the pin on your exact location</span>
                      <button
                        type="button"
                        onClick={useCurrentLocation}
                        disabled={locating}
                        className="normal-case tracking-normal text-xs font-bold inline-flex items-center gap-1 text-black hover:underline disabled:opacity-60"
                        data-testid="use-location-btn"
                      >
                        <Navigation className="w-3 h-3" /> {locating ? "Locating..." : "Use GPS"}
                      </button>
                    </div>
                    <PinDropMap
                      initialCenter={form.lat != null ? { lat: form.lat, lng: form.lng } : restaurant}
                      restaurant={restaurant}
                      onChange={handlePinChange}
                      height={260}
                    />
                    <div className="mt-2 flex items-center justify-between text-xs">
                      {formQuote?.within_service_area ? (
                        <div className="text-gray-600" data-testid="form-quote-line">
                          <span className="font-bold text-black">{formQuote.distance_km.toFixed(1)} km</span> · {formQuote.tier} · <span className="font-bold text-black">₹{formQuote.delivery_fee}</span>
                        </div>
                      ) : formQuote && !formQuote.within_service_area ? (
                        <div className="text-[#C85A5A] font-bold inline-flex items-center gap-1" data-testid="form-out-of-zone">
                          <AlertTriangle className="w-3 h-3" /> Out of delivery zone ({formQuote.distance_km.toFixed(1)} km)
                        </div>
                      ) : (
                        <div className="text-gray-400">Drag the map to place the pin…</div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <input data-testid="addr-label" className="border-b-2 border-gray-200 focus:border-black py-2 outline-none col-span-2" placeholder="Label (Home, Office...)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
                    <input data-testid="addr-line1" className="border-b-2 border-gray-200 focus:border-black py-2 outline-none col-span-2" placeholder="House / flat · building / street" value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} />
                    <input data-testid="addr-line2" className="border-b-2 border-gray-200 focus:border-black py-2 outline-none col-span-2" placeholder="Landmark (optional)" value={form.line2} onChange={(e) => setForm({ ...form, line2: e.target.value })} />
                    <input data-testid="addr-city" className="border-b-2 border-gray-200 focus:border-black py-2 outline-none" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                    <input data-testid="addr-pin" className="border-b-2 border-gray-200 focus:border-black py-2 outline-none" placeholder="Pincode" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} />
                    <input data-testid="addr-phone" className="border-b-2 border-gray-200 focus:border-black py-2 outline-none col-span-2" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </div>

                  <div className="flex gap-2">
                    <button onClick={saveAddress} disabled={!formQuote?.within_service_area} className="bg-black text-white rounded-full px-5 py-2 text-sm font-bold disabled:bg-gray-300" data-testid="save-address-btn">Save address</button>
                    <button onClick={() => { setShowNew(false); setFormQuote(null); }} className="rounded-full px-5 py-2 text-sm font-bold text-gray-600">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Payment */}
          <section>
            <h2 className="font-display font-bold text-xl mb-4">Payment</h2>
            <div className="space-y-3">
              <label className={`flex items-center gap-3 border-2 rounded-2xl p-4 cursor-pointer ${payment === "COD" ? "border-black bg-gray-50" : "border-gray-100"}`} data-testid="pay-cod">
                <input type="radio" checked={payment === "COD"} onChange={() => setPayment("COD")} className="accent-black" />
                <div>
                  <div className="font-bold">Cash on Delivery</div>
                  <div className="text-xs text-gray-500">Pay with cash when your food arrives.</div>
                </div>
              </label>
              <div className="border-2 rounded-2xl p-4 opacity-50 cursor-not-allowed border-gray-100">
                <div className="font-bold flex items-center gap-2">UPI <span className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Coming soon</span></div>
                <div className="text-xs text-gray-500">UPI payments will be available shortly.</div>
              </div>
            </div>
          </section>
        </div>

        {/* Summary */}
        <aside className="lg:sticky lg:top-24 h-fit bg-[#F7F7F7] rounded-2xl p-6">
          <h3 className="font-display font-bold text-lg mb-4">Order summary</h3>
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {items.map((i) => (
              <div key={i.key} className="flex justify-between text-sm">
                <div>{i.qty} × {i.name}{i.variant ? <span className="text-gray-500"> ({i.variant})</span> : null}</div>
                <div className="font-medium">₹{i.price * i.qty}</div>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-5 border-t border-gray-200 space-y-1.5 text-sm">
            <div className="flex justify-between" data-testid="food-total-line">
              <span className="text-gray-600">Food Total</span>
              <span>₹{subtotal}</span>
            </div>

            {/* Delivery charge row with distance */}
            <div className="flex justify-between items-start" data-testid="delivery-charge-line">
              <div>
                <div className="text-gray-600">Delivery Charge</div>
                {quoting && <div className="text-[11px] text-gray-400">Calculating…</div>}
                {quote?.within_service_area && (
                  <div className="text-[11px] text-gray-400" data-testid="distance-line">
                    {quote.distance_km.toFixed(1)} km · {quote.tier}
                  </div>
                )}
              </div>
              <span data-testid="delivery-fee">{quote?.within_service_area ? `₹${quote.delivery_fee}` : "—"}</span>
            </div>

            {quote && !quote.within_service_area && (
              <div className="rounded-xl bg-red-50 border border-[#C85A5A]/30 p-3 text-xs text-[#C85A5A] flex gap-2 items-start" data-testid="out-of-zone">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>{quote.message}</div>
              </div>
            )}

            <div className="flex justify-between pt-3 border-t border-dashed border-gray-300 font-black text-lg" data-testid="cod-payable-line">
              <span>{payment === "COD" ? "COD Payable" : "Total Payable"}</span>
              <span data-testid="summary-total">₹{total}</span>
            </div>
          </div>
          {!storeOpen && (
            <div
              className="mt-5 rounded-2xl bg-red-50 border-2 border-[#C85A5A] p-4 text-center"
              data-testid="checkout-closed-alert"
            >
              <div className="font-display font-black text-base text-[#C85A5A]">Sorry, we are closed today!</div>
              <div className="text-xs text-gray-600 mt-1">Please check back during our open hours.</div>
            </div>
          )}
          <button
            onClick={placeOrder}
            disabled={submitting || !canPlace}
            className="mt-5 w-full bg-black text-white rounded-full py-4 font-bold hover:bg-gray-800 disabled:bg-gray-300 transition inline-flex items-center justify-center gap-2"
            data-testid="place-order-btn"
          >
            <MapPin className="w-4 h-4" />
            {submitting ? "Placing order..." : !storeOpen ? "Closed — orders paused" : "Place Order"}
          </button>
          <p className="text-xs text-gray-500 mt-3 text-center">By placing order you agree to our fair-use terms.</p>
        </aside>
      </div>
    </div>
  );
}
